import { z } from 'zod';

import { ProductAvailabilityStatus } from '../../../enums';

export const ShopProductToolNames = {
  GetOperator: 'getOperator',
  GetShopProducts: 'getShopProducts',
  GetSimilarProducts: 'getSimilarProducts',
  RecommendShopProducts: 'recommendShopProducts',
  ShowShopProductDetail: 'showShopProductDetail',
  GetShopProductDetails: 'getShopProductDetails',
  GetShopProductByProductId: 'getShopProductByProductId',
  GetShopProductByExternalId: 'getShopProductByExternalId',
  GetShopProductByEan: 'getShopProductByEan',
  GetShopProductByIsbn: 'getShopProductByIsbn',
  GetShopCategories: 'getShopCategories',
  GetShopDeliveryOptions: 'getShopDeliveryOptions',
  GetShopProductAvailability: 'getShopProductAvailability',
  GetShopProductGifts: 'getShopProductGifts',
  GetShopProductAccessories: 'getShopProductAccessories',
  CompareShopProducts: 'compareShopProducts',
} as const;

/**
 * Tools that `getShopProductDetails` replaced. They are no longer registered
 * or offered, but their names, argument schemas and artifact types stay in
 * this contract: persisted conversations still carry calls under these names
 * and the widget has to keep rendering them, and stored chat allowlists that
 * mention them are normalized to the replacement rather than rejected.
 */
export const RetiredShopProductToolNames = [
  ShopProductToolNames.GetShopProductByProductId,
  ShopProductToolNames.GetShopProductByExternalId,
  ShopProductToolNames.GetShopProductByEan,
  ShopProductToolNames.GetShopProductByIsbn,
  ShopProductToolNames.GetShopProductAvailability,
  ShopProductToolNames.GetShopProductGifts,
  ShopProductToolNames.GetShopProductAccessories,
  ShopProductToolNames.CompareShopProducts,
] as const;

export type RetiredShopProductToolName =
  (typeof RetiredShopProductToolNames)[number];

/**
 * Schema for preview of a product. Used in arrays,
 * carousels, get accessories, etc.
 */
export const shopProductPreviewSchema = z.object({
  id: z.string(),
  name: z.string(),
  title: z.string().nullish(),
  /** Null when the shop publishes no price: say "price not available", never guess or imply it is free. */
  price: z.number().nullish(),
  /** ISO 4217 currency code from the feed (e.g. CZK, EUR). */
  currency: z.string().nullish(),
  imageUrl: z.string().nullish(),
  images: z.array(z.string()).nullish(),
  url: z.string(),
  description: z.string().nullish(),
});

export type ShopProductPreview = z.infer<typeof shopProductPreviewSchema>;

const numericFilterSchema = z
  .object({
    name: z
      .string()
      .describe(
        'Attribute name exactly as it appears in availableFilters or in a candidate\'s attributes. Display names are accepted too and matching ignores case ("Objem" finds "objem").',
      ),
    min: z
      .number()
      .finite()
      .nullish()
      .describe(
        'Inclusive lower bound, in the attribute\'s own unit — the one the catalog shows for it, e.g. `Šířka (cm)` is filtered in centimetres and `Objem (l)` in litres. Convert the user\'s figure into that unit first: "at least 500 ml" on a litre attribute is 0.5. Omit for no lower bound.',
      ),
    max: z
      .number()
      .finite()
      .nullish()
      .describe(
        'Inclusive upper bound, in the same unit as `min`. Omit for no upper bound.',
      ),
  })
  .superRefine((filter, ctx) => {
    if (filter.min == null && filter.max == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Set at least one of min or max',
      });
    }

    if (filter.min != null && filter.max != null && filter.min > filter.max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'min must be less than or equal to max',
      });
    }
  });

/**
 * GetShopProducts tool contracts
 */
export const getShopProductsArgsSchema = z.object({
  query: z
    .string()
    .nullish()
    .describe(
      'Full, unmodified search query from the user. Do not simplify, shorten, or modify the search term as the entire query is used for semantic and keyword search. Prefer this for broad discovery before applying strict filters. For recommendation-style requests, start with query-first search and refine in follow-up calls only if needed.',
    ),
  searchType: z
    .enum(['keywords', 'semantic', 'balanced'])
    .nullish()
    .describe(
      'Retrieval bias for the query. "balanced" (the default when omitted) is a mixed keyword+vector search and is the right choice for most queries. Use "keywords" when the query is a named product, a brand, a code, or a quoted exact phrase. Use "semantic" for use-case, gift, or conceptual queries ("něco na bolavá záda", "dárek pro běžce", "something for back pain").',
    ),
  categoryIds: z
    .array(z.string())
    .nullish()
    .describe(
      'Nullish array of category IDs to filter by. Use when user explicitly requests categories or when refining broad searches. Caution: category filters can hide relevant semantic matches if applied too early. Best practice: run an initial broad query first, then apply categoryIds in refinement calls.',
    ),
  minPrice: z
    .number()
    .nullish()
    .describe(
      'Minimum price filter. Only include when user explicitly mentions a price range or minimum price. Format: raw number without currency symbol (e.g., 500 not 500 Kč).',
    ),
  maxPrice: z
    .number()
    .nullish()
    .describe(
      'Maximum price filter. Only include when user explicitly mentions a price range or maximum price. Format: raw number without currency symbol (e.g., 1000 not 1000 Kč).',
    ),
  isOnSale: z
    .boolean()
    .nullish()
    .describe(
      'Filter by products on sale. Set to true only when user explicitly asks for discounted/sale items. Do not set this parameter unless specifically requested.',
    ),
  inStockOnly: z
    .boolean()
    .nullish()
    .describe(
      'Set to true to return only products currently in stock. Use when the user asks for immediately available products.',
    ),
  brands: z
    .array(z.string())
    .nullish()
    .describe(
      'Only return products of these brands (exact brand names as they appear in earlier results). Use when the user asks for specific brands.',
    ),
  excludeBrands: z
    .array(z.string())
    .nullish()
    .describe(
      'Exclude products of these brands (exact brand names). Use when the shop instructions or the user forbid certain brands.',
    ),
  externalId: z.string().nullish().describe('External product ID'),
  ean: z.string().nullish().describe('EAN code of the product'),
  isbn: z.string().nullish().describe('ISBN code of the product'),
  sortBy: z
    .enum(['price', 'name'])
    .nullish()
    .describe(
      'Sort results by this field. Use "price" for price-sensitive queries ("cheapest…", "most expensive…"), or "name" for alphabetical listings. Works with or without `query` — with a query, relevant matches are found first and then sorted.',
    ),
  sortOrder: z
    .enum(['asc', 'desc'])
    .nullish()
    .describe(
      'Sort direction. Use "asc" for lowest-to-highest price or A-Z naming. Use "desc" for highest-to-lowest price or Z-A naming.',
    ),
  attributes: z
    .array(
      z.object({
        name: z
          .string()
          .describe(
            'Attribute name exactly as it appears in availableFilters or in a candidate\'s attributes. Display names are accepted too and matching ignores case ("Barva" finds "barva").',
          ),
        values: z
          .array(z.string())
          .min(1)
          .describe(
            'Accepted values for this attribute, spelled exactly as the catalog stores them — copy them from availableFilters rather than translating or reformatting them, because matching is exact. Listing several is an OR: a product qualifies by carrying ANY one of them.',
          ),
      }),
    )
    .nullish()
    .describe(
      'Attribute filters, combined the way a shop facet is: the values inside ONE entry are alternatives (OR), while separate entries must ALL hold (AND). One entry of name "barva" with values ["modrá", "červená"] returns blue or red products; adding a second entry for "objem" returns only those that are also that volume. Repeating a name in two entries means the same as listing both values in one. Use it as a follow-up refinement once a search has reported availableFilters. A name this catalog does not know is reported back to you instead of quietly matching nothing; a value it does not store simply matches nothing, so copy values from availableFilters.',
    ),
  numericFilters: z
    .array(numericFilterSchema)
    .max(4)
    .nullish()
    .describe(
      "Numeric range filters over product parameters (volume, length, weight, wattage, …). Set at least one of min/max per entry; several entries and the other filters all apply together (AND). Works ONLY for attributes this feed provides as real numbers — a text-only parameter cannot be range-filtered, and the tool tells you which numeric attributes exist (with their units) if the name does not resolve. Bounds are read in the attribute's unit, which availableFilters reports.",
    ),
  page: z
    .number()
    .int()
    .min(1)
    .nullish()
    .describe(
      'Which window of the ranked results to return, 1-based (default 1). Each page holds the next `limit` best-ranked candidates. To show the user more options, repeat the same query, filters and limit with page + 1; a different query makes the page number meaningless.',
    ),
  limit: z
    .number()
    .int()
    .min(1)
    .max(50)
    .nullish()
    .describe(
      'How many candidates one page holds (default 20, max 50). A smaller number fits an exact name or code; a larger one a broad search where you want to compare more products at once.',
    ),
});

export const getShopProductsContentSchema = z.string();
export const getShopProductsArtifactSchema = z
  .object({
    totalCount: z.number(),
    hasMore: z.boolean(),
    products: z.array(shopProductPreviewSchema),
    categories: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          displayName: z.string().nullish(),
        }),
      )
      .nullish(),
    availableFilters: z
      .array(
        z.object({
          name: z.string(),
          displayName: z.string().nullish(),
          values: z.array(z.string()),
          /** Set on numeric attributes: the unit `numericFilters` bounds are read in. */
          numericUnit: z.string().nullish(),
          numeric: z.boolean().optional(),
        }),
      )
      .nullish(),
  })
  .nullish();

export type GetShopProductsArgs = z.infer<typeof getShopProductsArgsSchema>;
export type GetShopProductsContent = z.infer<
  typeof getShopProductsContentSchema
>;
export type GetShopProductsArtifact = z.infer<
  typeof getShopProductsArtifactSchema
>;

/**
 * GetSimilarProducts tool contracts.
 *
 * Research-only neighbourhood lookup on the identity vector. It returns the
 * same candidate payload as `getShopProducts` on purpose — a third product
 * shape would only teach the model a third set of field names for the same
 * thing.
 */
export const getSimilarProductsArgsSchema = z.object({
  productId: z
    .string()
    .uuid()
    .describe(
      'Internal product ID (UUID) of the product to find neighbours for, exactly as returned by an earlier tool result. The product itself is never among the results.',
    ),
  limit: z
    .number()
    .nullish()
    .describe(
      'How many neighbouring products to return for evaluation (default 8, max 20).',
    ),
});

export const getSimilarProductsContentSchema = z.string();

export type GetSimilarProductsArgs = z.infer<
  typeof getSimilarProductsArgsSchema
>;
export type GetSimilarProductsContent = z.infer<
  typeof getSimilarProductsContentSchema
>;

/**
 * RecommendShopProducts tool contracts.
 *
 * The only way product cards reach the user: the model picks the products
 * (by internal ID, from prior search/detail results) and the backend
 * hydrates all card data from the database — the model can never fabricate
 * a product, price, image, or URL on a card.
 */
export const recommendShopProductsArgsSchema = z.object({
  products: z
    .array(
      z.object({
        productId: z
          .string()
          .describe(
            'Internal product ID exactly as returned by an earlier tool result in this conversation.',
          ),
        reason: z
          .string()
          .nullish()
          .describe(
            "One short sentence, in the user's language, on why this product fits their request (shown to the user under the card).",
          ),
      }),
    )
    .min(1)
    .max(10)
    .describe(
      'Products to show to the user as cards, best matches first. Include every product that genuinely matches the user request and the shop instructions — do not artificially limit the selection when several fit well.',
    ),
});

export const recommendShopProductsContentSchema = z.string();
export const recommendShopProductsArtifactSchema = z
  .object({
    products: z.array(
      shopProductPreviewSchema.extend({
        reason: z.string().nullish(),
      }),
    ),
  })
  .nullish();

export type RecommendShopProductsArgs = z.infer<
  typeof recommendShopProductsArgsSchema
>;
export type RecommendShopProductsContent = z.infer<
  typeof recommendShopProductsContentSchema
>;
export type RecommendShopProductsArtifact = z.infer<
  typeof recommendShopProductsArtifactSchema
>;

/**
 * ShowShopProductDetail tool contracts.
 *
 * Presenter counterpart to the research-only detail lookups: renders one
 * product's full detail card (images, attributes, categories) to the user.
 * Data is hydrated server-side by ID — same anti-fabrication guarantee as
 * recommendShopProducts.
 */
export const showShopProductDetailArgsSchema = z.object({
  productId: z
    .string()
    .describe(
      'Internal product ID (from earlier tool results) of the single product whose full detail card should be shown to the user.',
    ),
});

export const showShopProductDetailContentSchema = z.string();

export type ShowShopProductDetailArgs = z.infer<
  typeof showShopProductDetailArgsSchema
>;
export type ShowShopProductDetailContent = z.infer<
  typeof showShopProductDetailContentSchema
>;

/** How many products one `getShopProductDetails` call may load by id. */
export const SHOP_PRODUCT_DETAILS_MAX_IDS = 4;

/**
 * Full research detail for known products: by internal ids from earlier
 * results, or by one catalog identifier. Exactly one of the four keys is set
 * per call.
 */
export const getShopProductDetailsArgsSchema = z.object({
  productIds: z
    .array(z.string().uuid())
    .min(1)
    .max(SHOP_PRODUCT_DETAILS_MAX_IDS)
    .nullish()
    .describe(
      'Internal product IDs (UUID) copied exactly from earlier tool results, 1 to 4. Pass several to compare products side by side.',
    ),
  externalId: z
    .string()
    .nullish()
    .describe(
      'Product code as the shop or customer writes it (external ID / SKU). Returns every active product carrying that code.',
    ),
  ean: z
    .string()
    .nullish()
    .describe('EAN barcode. Returns every active product carrying it.'),
  isbn: z
    .string()
    .nullish()
    .describe('ISBN (books). Returns every active product carrying it.'),
});

export const getShopProductDetailsContentSchema = z.string();

export type GetShopProductDetailsArgs = z.infer<
  typeof getShopProductDetailsArgsSchema
>;
export type GetShopProductDetailsContent = z.infer<
  typeof getShopProductDetailsContentSchema
>;

/**
 * Retired identifier lookups (productId, externalId, ean, isbn), kept for
 * rendering persisted conversations — see `RetiredShopProductToolNames`.
 */
export const getShopProductByProductIdArgsSchema = z.object({
  productId: z
    .string()
    .uuid()
    .describe(
      'The internal product ID (UUID). Use for a known single product detail lookup, not for comparisons. If user asks about 2+ products, collect IDs and use compareShopProducts instead.',
    ),
});

export const getShopProductByExternalIdArgsSchema = z.object({
  externalId: z
    .string()
    .describe(
      'The external product ID. Use for a known single product detail lookup, not for comparisons. If user asks about 2+ products, collect IDs and use compareShopProducts instead.',
    ),
});

export const getShopProductByEanArgsSchema = z.object({
  ean: z
    .string()
    .describe(
      'The EAN code of the product. Use for a known single product detail lookup, not for comparisons. If user asks about 2+ products, collect IDs and use compareShopProducts instead.',
    ),
});

export const getShopProductByIsbnArgsSchema = z.object({
  isbn: z
    .string()
    .describe(
      'The ISBN code of the product. Use for a known single product detail lookup, not for comparisons. If user asks about 2+ products, collect IDs and use compareShopProducts instead.',
    ),
});

/**
 * These are identical for all tools, so we can reuse the same schema.
 */
export const getShopProductContentSchema = z.string();
const productFeedEvidenceSchema = z
  .object({
    deliveryDateRaw: z.string().optional(),
    stockQuantityRaw: z.record(z.string()).optional(),
    customFields: z.record(z.string()).optional(),
  })
  .optional();

export const getShopProductArtifactSchema = z.object({
  feedEvidence: productFeedEvidenceSchema,
  id: z.string(),
  name: z.string(),
  title: z.string().nullish(),
  /** Null when the shop publishes no price ("price not available"). */
  price: z.number().nullish(),
  /** ISO 4217 currency code from the feed (e.g. CZK, EUR). */
  currency: z.string().nullish(),
  brand: z.string().nullish(),
  description: z.string().nullish(),
  /**
   * Enrichment's ~200-word normalized summary. Sits alongside the raw
   * description rather than replacing it: the description is what the
   * merchant asserts, the summary is what the catalog understands. The deep
   * dive loads exactly one product, so there is no per-candidate token cost
   * to carrying both.
   */
  shortSummary: z.string().nullish(),
  url: z.string(),
  imageUrl: z.string().nullish(),
  images: z.array(z.string()).nullish(),
  /**
   * Per-image descriptions, index-aligned with `[imageUrl, ...images]`.
   *
   * The only path by which the assistant can answer "what colour is it" or
   * "what is in the box" for a catalog whose feed text never says. A vision
   * model is already paid to produce these at import; before this they
   * reached the lexical index and the admin UI but never the assistant.
   */
  imageAnnotations: z.array(z.string()).nullish(),
  categories: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        displayName: z.string().nullish(),
      }),
    )
    .nullish(),
  gifts: z.array(z.string()).nullish(),
  /**
   * Extended warranty beyond the statutory one, when the feed declares one.
   * `null` means the feed reported none — not "unknown" — the same
   * present-key-null-value convention `gifts` above already uses.
   */
  extendedWarranty: z
    .object({
      /** Warranty length in months, beyond the statutory one. */
      months: z.number(),
      /** What the extended warranty actually covers. */
      description: z.string(),
    })
    .nullish(),
  /** Merchant services attached to the product — installation, disposal, assembly. */
  specialServices: z.array(z.string()).nullish(),
  accessories: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        title: z.string().nullish(),
        url: z.string(),
      }),
    )
    .nullish(),
  attributes: z.array(
    z.object({
      id: z.string(),
      value: z.string(),
      attribute: z.object({
        id: z.string(),
        displayName: z.string().nullish(),
        name: z.string(),
      }),
    }),
  ),
});

/**
 * Outcome of an identifier lookup (productId / externalId / EAN / ISBN).
 * Feeds routinely reuse a code across variants or duplicated rows, so
 * "ambiguous" is a real catalog state the assistant has to relay — silently
 * returning one of several rows made it describe the wrong SKU.
 */
export const shopProductLookupStatusSchema = z.enum([
  'unique',
  'ambiguous',
  'not_found',
]);

export const shopProductLookupResultSchema = z.object({
  status: shopProductLookupStatusSchema,
  /** Active products carrying the identifier; may exceed `matches.length`. */
  matchCount: z.number().int(),
  matches: z.array(getShopProductArtifactSchema),
});

export type ShopProductLookupStatus = z.infer<
  typeof shopProductLookupStatusSchema
>;
export type ShopProductLookupResult = z.infer<
  typeof shopProductLookupResultSchema
>;

export type GetShopProductByProductIdArgs = z.infer<
  typeof getShopProductByProductIdArgsSchema
>;
export type GetShopProductByExternalIdArgs = z.infer<
  typeof getShopProductByExternalIdArgsSchema
>;
export type GetShopProductByEanArgs = z.infer<
  typeof getShopProductByEanArgsSchema
>;
export type GetShopProductByIsbnArgs = z.infer<
  typeof getShopProductByIsbnArgsSchema
>;

export type GetShopProductContent = z.infer<typeof getShopProductContentSchema>;
export type GetShopProductArtifact = z.infer<
  typeof getShopProductArtifactSchema
>;

/**
 * GetShopCategories tool contracts
 */
export const getShopCategoriesArgsSchema = z.object({
  parentCategoryId: z
    .string()
    .nullish()
    .describe(
      'Optional parent category ID to fetch subcategories. Use this to iteratively narrow category browsing.',
    ),
});

export const shopCategoryPreviewSchema = z.object({
  id: z.string(),
  name: z.string(),
  displayName: z.string().nullish(),
  /** Category page URL when the feed provides one — safe to share with users. */
  url: z.string().nullish(),
});

export const getShopCategoriesContentSchema = z.string();
export const getShopCategoriesArtifactSchema = z.object({
  categories: z.array(
    shopCategoryPreviewSchema.merge(
      z.object({
        children: z.array(shopCategoryPreviewSchema).nullish(),
      }),
    ),
  ),
});

export type ShopCategoryPreview = z.infer<typeof shopCategoryPreviewSchema>;
export type GetShopCategoriesArgs = z.infer<typeof getShopCategoriesArgsSchema>;
export type GetShopCategoriesContent = z.infer<
  typeof getShopCategoriesContentSchema
>;
export type GetShopCategoriesArtifact = z.infer<
  typeof getShopCategoriesArtifactSchema
>;

/**
 * GetShopDeliveryOptions tool contracts
 */
export const getShopDeliveryOptionsArgsSchema = z.object({
  productId: z
    .string()
    .describe(
      'The product ID to get delivery options for. Call after identifying the specific product.',
    ),
});

export const getShopDeliveryOptionsContentSchema = z.string();
export const getShopDeliveryOptionsArtifactSchema = z.object({
  productId: z.string(),
  deliveryDate: z.string().nullish(),
  dispatchLeadTimeDays: z.number().int().nonnegative().nullish(),
  availabilityDate: z.string().nullish(),
  deliveryOptions: z
    .array(
      z.object({
        carrierId: z.string(),
        /** Human-readable carrier label derived from the feed carrier code. */
        carrier: z.string().nullish(),
        price: z.number().nullish(),
        priceCod: z.number().nullish(),
      }),
    )
    .nullish(),
});

export type GetShopDeliveryOptionsArgs = z.infer<
  typeof getShopDeliveryOptionsArgsSchema
>;
export type GetShopDeliveryOptionsContent = z.infer<
  typeof getShopDeliveryOptionsContentSchema
>;
export type GetShopDeliveryOptionsArtifact = z.infer<
  typeof getShopDeliveryOptionsArtifactSchema
>;

/**
 * GetShopProductAvailability tool contracts
 */
export const getShopProductAvailabilityArgsSchema = z.object({
  productId: z
    .string()
    .describe(
      'The product ID to get stock/availability for. Call after identifying the specific product.',
    ),
});

export const getShopProductAvailabilityContentSchema = z.string();
export const getShopProductAvailabilityArtifactSchema = z.object({
  feedEvidence: productFeedEvidenceSchema,
  productId: z.string(),
  stockQuantity: z.number().int().nullish(),
  dispatchLeadTimeDays: z.number().int().nonnegative().nullish(),
  availability: z.nativeEnum(ProductAvailabilityStatus).nullish(),
});

export type GetShopProductAvailabilityArgs = z.infer<
  typeof getShopProductAvailabilityArgsSchema
>;
export type GetShopProductAvailabilityContent = z.infer<
  typeof getShopProductAvailabilityContentSchema
>;
export type GetShopProductAvailabilityArtifact = z.infer<
  typeof getShopProductAvailabilityArtifactSchema
>;

/**
 * GetShopProductGifts tool contracts
 */
export const getShopProductGiftsArgsSchema = z.object({
  productId: z
    .string()
    .describe(
      'The product ID to get gifts for. Call after identifying the specific product.',
    ),
});

export const getShopProductGiftsContentSchema = z.string();
export const getShopProductGiftsArtifactSchema = z.object({
  productId: z.string(),
  gifts: z.array(z.string()),
});

export type GetShopProductGiftsArgs = z.infer<
  typeof getShopProductGiftsArgsSchema
>;
export type GetShopProductGiftsContent = z.infer<
  typeof getShopProductGiftsContentSchema
>;
export type GetShopProductGiftsArtifact = z.infer<
  typeof getShopProductGiftsArtifactSchema
>;

/**
 * GetShopProductAccessories tool contracts
 */
export const getShopProductAccessoriesArgsSchema = z.object({
  productId: z
    .string()
    .describe(
      'The product ID to get accessories for. Call after identifying the specific product.',
    ),
});

export const getShopProductAccessoriesContentSchema = z.string();
export const getShopProductAccessoriesArtifactSchema = z.object({
  productId: z.string(),
  accessories: z.array(shopProductPreviewSchema),
});

export type GetShopProductAccessoriesArgs = z.infer<
  typeof getShopProductAccessoriesArgsSchema
>;
export type GetShopProductAccessoriesContent = z.infer<
  typeof getShopProductAccessoriesContentSchema
>;
export type GetShopProductAccessoriesArtifact = z.infer<
  typeof getShopProductAccessoriesArtifactSchema
>;

/**
 * GetOperator tool contracts
 */
export const getOperatorContentSchema = z.string();

export const getOperatorTodayHoursSchema = z.object({
  /** True when today is marked closed or has no open intervals. */
  closed: z.boolean(),
  /** Clock ranges for today, already local HH:mm strings for display. */
  ranges: z
    .array(
      z.object({
        from: z.string(),
        to: z.string(),
      }),
    )
    .optional(),
});

export const getOperatorArtifactSchema = z.object({
  operator: z
    .object({
      id: z.string(),
      firstName: z.string().nullish(),
      lastName: z.string().nullish(),
      email: z.string().nullish(),
      phone: z.string().nullish(),
    })
    .nullish(),
  /**
   * Live chat connect is offered only while this is true. Absent on legacy
   * rows — treat as available so old handoffs keep their connect button.
   */
  available: z.boolean().optional(),
  /** Today's opening intervals (or closed), for the card and the model. */
  todayHours: getOperatorTodayHoursSchema.nullish(),
});

export type GetOperatorArtifact = z.infer<typeof getOperatorArtifactSchema>;
export type GetOperatorContent = z.infer<typeof getOperatorContentSchema>;
export type GetOperatorTodayHours = z.infer<typeof getOperatorTodayHoursSchema>;

/**
 * Shop product comparison tool contracts
 */
export const compareShopProductsArgsSchema = z.object({
  productIds: z
    .array(z.string())
    .min(2)
    .max(5)
    .describe(
      'Array of product IDs to compare. Min 2, max 5 products. Put all requested products into one call; do not split one comparison request across multiple calls. Use this both for explicit comparisons ("compare A vs B") and multi-product info requests ("tell me about A, B, C").',
    ),
  highlightDifferences: z
    .boolean()
    .nullish()
    .default(true)
    .describe(
      'If true, the comparison will focus on highlighting differences between products.',
    ),
});

export const compareShopProductsContentSchema = z.string();
export const compareShopProductsArtifactSchema = z.object({
  products: z.array(getShopProductArtifactSchema),
  comparisonMatrix: z.array(
    z.object({
      attribute: z.string().describe('Name of the compared attribute'),
      displayName: z
        .string()
        .nullish()
        .describe('Human-readable name of the attribute'),
      values: z.array(
        z.object({
          productId: z.string(),
          value: z.string().nullish(),
          isDifferent: z.boolean().nullish(),
        }),
      ),
      isDifferent: z.boolean().nullish(),
    }),
  ),
});

export type CompareShopProductsArgs = z.infer<
  typeof compareShopProductsArgsSchema
>;
export type CompareShopProductsContent = z.infer<
  typeof compareShopProductsContentSchema
>;
export type CompareShopProductsArtifact = z.infer<
  typeof compareShopProductsArtifactSchema
>;
